import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { safeRest, uniqueId, isControlled as isControlledFn } from '@wds/utils';
import { SIZES, INPUT_COLORS } from '@wds/helpers';
import { Label } from '@wds/internal-components';

import { useTheme } from 'react-jss';

import useStyles from './useStyles';

const Textarea = ({
  value,
  color,
  label,
  onBlur,
  onChange,
  id,
  name,
  stretched,
  size,
  disabled,
  ...rest
}) => {
  const [val, setVal] = useState(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isControlled = useMemo(() => isControlledFn(value), []);
  const inputID = useMemo(() => id || uniqueId('textarea'), [id]);

  const theme = useTheme();
  const classes = useStyles({ color, size, stretched, disabled, theme });

  const handleBlur = (e) => {
    if (onBlur) onBlur(e);
  };

  const handleChange = (e) => {
    if (isControlled && e.target.value !== val) {
      e.persist();
      setVal(e.target.value);
      if (onChange) onChange(e);
    }
  };

  return (
    <>
      <If condition={label}>
        <Label htmlFor={inputID}>{label}</Label>
      </If>
      <textarea
        className={classes.wrenchTextarea}
        id={inputID}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        value={val}
        {...safeRest(rest)}
      />
    </>
  );
};

Textarea.propTypes = {
  /**
   * If true, input is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Identifer if Label or ariaLabel is not provided
   */
  // eslint-disable-next-line consistent-return
  id: (props, propName) => {
    if (
      typeof props[propName] !== 'string' &&
      typeof props.label !== 'string' &&
      typeof props.ariaLabel !== 'string'
    ) {
      return new Error(
        'Accessibility Error: If `label` or `ariaLabel` is not provided the `id` prop is required.'
      );
    }
  },
  /**
   * Label for input
   */
  label: PropTypes.node,
  /**
   * Define name for input
   */
  name: PropTypes.string,
  /**
   * onBlur event handler
   * function(event: object) => void
   */
  onBlur: PropTypes.func,
  /**
   * onChange event handler
   * function(event: object) => void
   */
  onChange: PropTypes.func,
  /**
   * Size variants for TextArea
   */
  size: PropTypes.oneOf(SIZES),
  /**
   * If true, will fit the full width of parent container
   */
  stretched: PropTypes.bool,
  /**
   * Color variants for component
   */
  color: PropTypes.oneOf(INPUT_COLORS),
  /**
   * Pass prop value of input
   */
  value: PropTypes.node,
};

Textarea.defaultProps = {
  disabled: false,
  id: undefined,
  label: undefined,
  name: undefined,
  onBlur: undefined,
  onChange: undefined,
  size: 'md',
  stretched: false,
  color: undefined,
  value: undefined,
};

export default Textarea;
